import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import { createStructuredSelector } from 'reselect';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import get from 'lodash/get';
import { Grid, useMediaQuery } from '@material-ui/core';
import { Router, useTranslation } from '../../src/i18n';
import { viewBlocks, sectionBlocks } from './constants';
import Layout, { LayoutContent } from '../../components/Layout';
import { selectHomePerformances, selectArtists, selectHighlights } from './selectors';
import { colors } from '../../constants/styleConstants';
import route from '../../constants/routes';
import { defaultListPropType, useList } from '../../utils/crudUtils';
import bitmap from '../../public/images/bitmap.jpg';
import { getFlatFormValues } from '../../utils/formUtils';
import { selectIsLoggedInState } from '../../store/selectors';
import { filterViewMode, InteractiveFilterPanel } from '../InteractiveFilterPanel';
import { createDate } from '../../utils/date';
import { dateFormats } from '../../constants/consts';

const ProductionAccordion = dynamic(() => import('../../components/ProductionAccordion'));
const ArtistCard = dynamic(() => import('../../components/ArtistCard'));
const ArtistsSearchFilters = dynamic(() => import('../../components/ArtistsSearchFilters'));
const SearchBg = dynamic(() => import('../../components/SearchBg'));
const NextLink = dynamic(() => import('../../components/NextLink'));
const TitledDescription = dynamic(() => import('../../components/TitledDescription'));
const GoToLink = dynamic(() => import('../../components/GoToLink'));

export const useStyles = makeStyles(theme => ({
  sectionsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    paddingTop: 36,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      paddingTop: 20,
    },
  },
  sectionTitle: {
    color: colors.title,
    fontSize: '2.33rem',
    lineHeight: 1.1,
    marginBottom: 15,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  section: {
    width: '100%',
    marginTop: 30,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  viewAll: {
    fontWeight: 'bold',
  },
  sectionRow: {
    display: 'flex',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  rowItem: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    maxWidth: '32%',
    border: '1px solid black',
    background: 'gray',
    padding: '50px 10px 10px 20px',
    marginRight: 20,
    '&:last-of-type': {
      marginRight: 0,
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%',
      marginBottom: 20,
      marginRight: 0,
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
  },
  sectionBg: {
    height: 'auto',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    padding: '53px 20px',
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      backgroundPositionX: '-200px !important',
    },
    [theme.breakpoints.down('sm')]: {
      backgroundPositionX: '-70px !important',
    },
  },
  viewTitle: {
    fontSize: '40px',
    color: colors.white,
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 20,
    },
  },
  titleContainer: {
    width: '25%',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      width: '50%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  viewItemContainer: {
    width: '75%',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      width: '50%',
      flexWrap: 'wrap',
    },
    [theme.breakpoints.down('sm')]: {
      width: '50%',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  viewItem: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    maxWidth: '33%',
    padding: '0 15px',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      marginBottom: 20,
      padding: 0,
    },
  },
  // search
  performanceSection: {
    padding: '0 25px 0 0',
    borderRight: `1px solid ${colors.background.dark}`,
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 48%',
    maxWidth: '48%',
    [theme.breakpoints.down('sm')]: {
      flex: '1 0 100%',
      maxWidth: '100%',
      borderRight: 0,
      padding: 0,
    },
  },
  interactiveFilterPanel: {
    background: colors.white,
    boxShadow: 'none',
  },
  artistSection: {
    paddingLeft: '25px',
    flex: '1 0 52%',
    maxWidth: '52%',
    [theme.breakpoints.down('sm')]: {
      marginTop: 30,
      paddingLeft: 0,
      flex: '1 0 100%',
      maxWidth: '100%',
    },
  },
  searchContainer: {
    marginTop: '27px',
  },
  artistCard: {
    padding: '11px !important',
    [theme.breakpoints.down('sm')]: {
      padding: '5px !important',
    },
  },
  gallery: {
    justifyContent: 'center',
  },
  viewAllItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.82rem',
    fontWeight: 'bold',
    width: '99px',
    height: '137px',
    border: `1px solid ${colors.contentDivider}`,
    margin: 11,
    [theme.breakpoints.down('xs')]: {
      width: 99,
      height: 137,
      margin: 5,
    },
  },
  // hightlights
  contentItem: {
    width: '100%',
  },
}));

export const homeSections = {
  performanceSearch: {
    id: 'performances',
    title: 'operabase.general.FN_PERFORMANCESEARCH',
  },
  artists: {
    id: 'artists',
    title: 'operabase.general.FN_ARTISTSEARCH',
  },
  highlights: {
    id: 'highlights',
    title: 'operabase.general.m_HIGHLIGHTS',
  },
  performances: {
    id: 'performances',
    title: 'operabase.general.FN_PERFORMANCESTODAY',
  },
};

export const Home = ({ performancesRaw, productionsList, artistsList, isLoggedIn, performanceDateRange }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const tabletView = useMediaQuery(theme.breakpoints.down('md'));
  const [viewSections, setViewSections] = React.useState(viewBlocks);
  const [linkSections, setLinksSections] = React.useState(sectionBlocks);
  // search
  const searchProfilesWithCriteria = values => {
    const query = getFlatFormValues(values);
    const queryString = get(query, 'query');
    Router.push({
      pathname: route.ARTISTS,
      query: queryString ? { query: queryString } : {},
    });
  };

  const [artists] = useList(artistsList);

  // performances
  const performances = get(performancesRaw, 'data', []);

  const today = createDate(new Date()).format(dateFormats.basic);

  React.useEffect(() => {
    setViewSections(viewBlocks);
    setLinksSections(sectionBlocks);
  }, [performancesRaw]);

  // Highlights
  const [productions] = useList(productionsList);

  return (
    <Layout
      title={t('operabase.general.m_HOME')}
      seo={{
        title: t('operabase.general.SEO_TITLE_HOMEPAGE'),
        description: t('operabase.general.SEO_DESC_HOMEPAGE'),
        ogTitle: t('operabase.general.SEO_OG_TITLE_HOMEPAGE'),
      }}
    >
      <SearchBg />

      <LayoutContent className={classes.sectionsBox}>
        <div className={classes.sectionRow}>
          <section className={classes.performanceSection}>
            <InteractiveFilterPanel
              subTitle={' '}
              className={classes.interactiveFilterPanel}
              viewMode={filterViewMode.COMPACT}
              onSubmit={values => {
                Router.push(
                  {
                    pathname: route.PRODUCTIONS,
                    query: values,
                  },
                  undefined,
                  {
                    shallow: false,
                  },
                );
              }}
              onClear={() => {}}
              selectedValues={{}}
              performanceDateRange={performanceDateRange}
            />
          </section>
          <section className={classes.artistSection}>
            <h4 className={classes.sectionTitle}>{t(get(homeSections, 'artists.title'))}</h4>
            <div className={classes.searchContainer}>
              <ArtistsSearchFilters
                fullWidth
                variant="search"
                t={t}
                submitFunc={v => searchProfilesWithCriteria(v)}
                initialValues=""
              />
              <Grid className={classes.gallery} container spacing={2} style={{ paddingTop: 20 }}>
                {artists.length > 0 &&
                  artists.map(artist => (
                    <Grid key={`artist-${get(artist, 'id')}`} className={classes.artistCard} item>
                      <ArtistCard artist={artist} size="small" />
                    </Grid>
                  ))}
                <NextLink className={classes.viewAllItem} href={route.ARTISTS} as={route.ARTISTS}>
                  {t('operabase.general.FN_VIEW_MORE')}
                </NextLink>
              </Grid>
            </div>
          </section>
        </div>
        {performances && performances?.length > 0 && (
          <section className={classes.section}>
            <div className={classes.sectionHeader}>
              <h4 className={classes.sectionTitle}>{t(get(homeSections, 'performances.title'))}</h4>
              <NextLink
                href={`${route.PRODUCTIONS}\\?date_from=${today}&date_to=${today}`}
                as={`${route.PRODUCTIONS}\\?date_from=${today}&date_to=${today}`}
                target="_blank"
                className={classes.viewAll}
              >
                {t('operabase.general.VIEW_ALL')}
              </NextLink>
            </div>
            <Grid container spacing={2}>
              <Grid className={classes.contentItem} item md={12}>
                <ProductionAccordion
                  productions={performances.slice(0, 3)}
                  isLoggedIn={isLoggedIn}
                  viewType="production"
                  className=""
                />
              </Grid>
            </Grid>
          </section>
        )}
        <section className={classes.section}>
          <div className={classes.sectionHeader}>
            <h4 className={classes.sectionTitle}>{t(get(homeSections, 'highlights.title'))}</h4>
            <NextLink href={route.HIGHLIGHTS} as={route.HIGHLIGHTS} target="_blank" className={classes.viewAll}>
              {t('operabase.general.VIEW_ALL')}
            </NextLink>
          </div>
          <Grid container spacing={2} justify="center">
            <Grid className={classes.contentItem} item md={12}>
              <ProductionAccordion
                productions={productions.slice(0, 3)}
                isLoggedIn={isLoggedIn}
                viewType="production"
                className=""
              />
            </Grid>
          </Grid>
        </section>
        <section className={classes.section}>
          <div className={classes.sectionRow}>
            {viewSections.map(item => (
              <div className={classes.rowItem} key={item.id}>
                <TitledDescription
                  descColor="white"
                  color="white"
                  title={t(item.title)}
                  size={tabletView ? 'small' : 'extraLarge'}
                  description={t(item.description)}
                />
                <GoToLink linkText={t(item.linkText)} linkProps={item.linkProps} size="large" color="white" />
              </div>
            ))}
          </div>
        </section>
        <section className={classes.section}>
          <div
            className={classes.sectionBg}
            style={{ background: `url(${bitmap})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }}
          >
            <div className={classes.titleContainer}>
              <h2 className={classes.viewTitle}>{t('operabase.general.FN_OPERAPROFESSIONAL')}</h2>
            </div>
            <div className={classes.viewItemContainer}>
              {linkSections.map(item => (
                <div className={classes.viewItem} key={item.id}>
                  <TitledDescription
                    title={t(item.title)}
                    color={mobileView ? 'white' : 'default'}
                    size={tabletView ? 'small' : 'large'}
                    description={t(item.description)}
                  />
                  <GoToLink linkText={t(item.linkText)} linkProps={item.linkProps} size="large" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </LayoutContent>
    </Layout>
  );
};

Home.propTypes = {
  productionsList: defaultListPropType,
  performancesRaw: defaultListPropType,
  artistsList: defaultListPropType,
  isLoggedIn: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  artistsList: selectArtists(),
  productionsList: selectHighlights(),
  performancesRaw: selectHomePerformances(),
  isLoggedIn: selectIsLoggedInState(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(Home);
